import {
    CheckBoxOutlineBlankOutlined,
    CheckBoxOutlined,
    ExpandMore,
    AddOutlined,
    AttachFileOutlined,
    DragIndicator
} from '@mui/icons-material';
import { Box, Checkbox, IconButton, Link, MenuItem, Popover, Tooltip, Typography, useTheme, Switch } from '@mui/material';
import dayjs from 'dayjs';
import React, { MouseEvent, useRef, useState } from 'react';
import { IRecordField, IRecordFieldsList } from 'ui-component/records/types';
import { phoneNumberCaptureRegex } from 'utils/stringHelpers';
import { ListValue } from 'views/backoffice/CustomLists/types';
import { FieldMenu } from '../AllPanelContainer/FieldMenu';
import { RenderEntity } from 'ui-component/records/RecordGrid/components/RenderEntity';

export type RenderViewFieldProps = {
    field: FieldToView;
    handleOpenViewObjectPanel: (field: IRecordField, fieldName: string, editable: boolean) => void;
    headerData?: IRecordFieldsList | null;
    onClick?: () => void;
    onDoubleClick?: () => void;
    isDraggable?: boolean;
    isDragging?: boolean;
    showFieldMenu?: boolean;
    rightHandPanelWidth?: number;
    isRecordViewer?: boolean;
    objectName?: string;
};

export type FieldToView = {
    label: string;
    dataType: IRecordField['dataType'];
    value: any;
    isBaseField?: boolean;
    options?: any[];
    customLabel?: string;
    id: string;
};

export const RenderViewField = ({
    field,
    handleOpenViewObjectPanel,
    headerData,
    onClick,
    onDoubleClick,
    isDraggable = false,
    isDragging = false,
    showFieldMenu = true,
    rightHandPanelWidth,
    isRecordViewer,
    objectName
}: RenderViewFieldProps) => {
    const theme = useTheme();
    const { label, dataType, value, options = [], customLabel } = field;
    const dataList = headerData?.[label as keyof IRecordFieldsList];
    const isListOfObjects = dataList && dataList?.listType?.objectDefinition?.id;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const popoverOpen = Boolean(anchorEl);
    const resizeField = rightHandPanelWidth && rightHandPanelWidth <= 350;

    const handleToggleMultipleSelectPanel = (event: MouseEvent<HTMLElement>) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const mainTypography = (text: string, handleClick?: () => void, isLink?: boolean) => (
        <Typography
            color={isLink ? theme.palette.primary.dark : theme.palette.primary[400]}
            fontWeight={500}
            sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textDecoration: isLink ? 'underline' : 'none',
                cursor: isLink ? 'pointer' : 'default'
            }}
            onClick={handleClick}
        >
            {text || ''}
        </Typography>
    );

    const popoverContent = (optionsList: ListValue[] | string[], isCheckbox?: boolean) => (
        <Popover
            anchorEl={anchorEl}
            open={popoverOpen}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
                sx: {
                    borderRadius: '0.5rem',
                    ml: 0,
                    mt: '0.3125rem',
                    width: containerRef.current?.clientWidth || '12.5rem'
                }
            }}
        >
            {optionsList.length > 0 &&
                optionsList.map((option: ListValue | string, idx) => {
                    const name =
                        typeof option === 'string' ? option : (option?.userValue?.name || option?.value || option || '').toString();

                    return (
                        <Tooltip title={name} key={`${name}-${idx}`}>
                            <Box>
                                <MenuItem
                                    sx={{
                                        height: '2.5rem',
                                        px: '0.75rem',
                                        bgcolor: theme.palette.background.paper
                                    }}
                                >
                                    {isCheckbox && (
                                        <Checkbox
                                            size="small"
                                            checked={
                                                Array.isArray(value) &&
                                                value.includes(Number(typeof option === 'string' ? option : option.id))
                                            }
                                            disabled
                                            icon={
                                                <CheckBoxOutlineBlankOutlined
                                                    sx={{ fontSize: '1.25rem', color: theme.palette.primary[400] }}
                                                />
                                            }
                                            checkedIcon={
                                                <CheckBoxOutlined sx={{ fontSize: '1.25rem', color: theme.palette.primary[400] }} />
                                            }
                                        />
                                    )}
                                    <Typography fontSize="0.875rem" sx={{ overflow: 'hidden', width: '100%', textOverflow: 'ellipsis' }}>
                                        {name || ''}
                                    </Typography>
                                </MenuItem>
                            </Box>
                        </Tooltip>
                    );
                })}
        </Popover>
    );

    const getFormattedValue = (type: string, text: any) => {
        if (type === 'date') {
            return text ? dayjs(text).format('YYYY/MM/DD') : '';
        }
        if (type === 'datetime') {
            return text ? dayjs(text).format('YYYY/MM/DD hh:mm A') : '';
        }
        if (type === 'time') {
            return text ? dayjs(text, 'HH:mm:ss:SSS').format('hh:mm A') : '';
        }

        if (type === 'phone') {
            if (!text) return '+1';
            const match = text.match(phoneNumberCaptureRegex);
            const formattedPhoneNum = match
                ? `+${match[1]}(${match[2]}) ${match[3]}-${match[4]}`
                : `+${value.replace(/[[\]]/g, '').replace(/,/, ' ')}`;

            return formattedPhoneNum;
        }

        return text || '';
    };

    const renderObjectProperty = () => {
        const activeDataType = typeof value === 'boolean' ? 'boolean' : dataType;

        switch (activeDataType) {
            case 'attachment':
                if (value) {
                    return (
                        <Typography
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                color: theme.palette.primary[400],
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <AttachFileOutlined />
                            Attachment
                        </Typography>
                    );
                }
                return (
                    <Typography
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            color: '#C6C4C4',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <AddOutlined />
                        Add Attachment
                    </Typography>
                );
            case 'boolean': {
                return (
                    <Switch
                        size="small"
                        checked={value === 'true'}
                        disabled
                        sx={{
                            '& .MuiSwitch-thumb': {
                                backgroundColor: theme.palette.background.paper
                            },
                            '& .MuiSwitch-track': {
                                backgroundColor: theme.palette.primary[400]
                            }
                        }}
                    />
                );
            }
            case 'checkbox': {
                const checkboxIconStyle = { fontSize: '1.25rem', color: theme.palette.primary[400] };
                return (
                    <Checkbox
                        size="small"
                        checked={value === 'true'}
                        disabled
                        icon={<CheckBoxOutlineBlankOutlined sx={checkboxIconStyle} />}
                        checkedIcon={<CheckBoxOutlined sx={checkboxIconStyle} />}
                    />
                );
            }
            case 'dropdown':
                if (isListOfObjects) {
                    return mainTypography(
                        value || '',
                        () => headerData && handleOpenViewObjectPanel(headerData[label as keyof IRecordFieldsList], label, false),
                        true
                    );
                }
                return mainTypography(value || '');
            case 'multiselect':
                if (!Array.isArray(value)) {
                    return mainTypography('');
                }
                return (
                    <Box ref={containerRef} onClick={handleToggleMultipleSelectPanel} width="100%">
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            {mainTypography(value.filter((item) => item != null).join(', ') || '')}
                            {value.length > 0 && <ExpandMore htmlColor={theme.palette.primary[400]} sx={{ cursor: 'pointer' }} />}
                        </Box>
                        {popoverOpen && popoverContent(value)}
                    </Box>
                );
            case 'multiselect-checkbox':
                return (
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        ref={containerRef}
                        onClick={handleToggleMultipleSelectPanel}
                        maxWidth="100%"
                    >
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            gap="0.5rem"
                            overflow="hidden"
                        >
                            {options.slice(0, 2)?.map((option: ListValue) => (
                                <Box key={option.id} display="flex" flexDirection="row" alignItems="center" gap={1}>
                                    <Box sx={{ maxWidth: '4rem', display: 'flex', alignItems: 'center' }}>
                                        {mainTypography((option.userValue?.name || option.value || '').toString())}
                                        <Checkbox
                                            size="small"
                                            checked={Array.isArray(value) && value.includes(Number(option.id))}
                                            disabled
                                            icon={
                                                <CheckBoxOutlineBlankOutlined
                                                    sx={{ fontSize: '1.25rem', color: theme.palette.primary[400] }}
                                                />
                                            }
                                            checkedIcon={
                                                <CheckBoxOutlined sx={{ fontSize: '1.25rem', color: theme.palette.primary[400] }} />
                                            }
                                        />
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        {options.length > 0 && <ExpandMore htmlColor={theme.palette.primary[400]} sx={{ cursor: 'pointer' }} />}
                        {popoverOpen && popoverContent(options, true)}
                    </Box>
                );
            case 'object': {
                const objectValueFormatted = value?.replace('true', 'Yes').replace('false', 'No');

                return mainTypography(
                    objectValueFormatted,
                    () => headerData && handleOpenViewObjectPanel(headerData[label as keyof IRecordFieldsList], label, false),
                    true
                );
            }

            case 'url': {
                let url = value;
                if (url && !/^https?:\/\//i.test(url)) {
                    url = `https://${url}`;
                }
                return (
                    <Box ref={containerRef} width="100%" display="flex" alignItems="center" gap="0.5rem" justifyContent="space-between">
                        {mainTypography(value || '')}

                        {value && (
                            <IconButton sx={{ p: 0 }} onMouseEnter={handleToggleMultipleSelectPanel}>
                                <AttachFileOutlined
                                    sx={{ fontSize: '1.25rem', color: theme.palette.primary[600], rotate: '30deg', cursor: 'pointer' }}
                                />
                            </IconButton>
                        )}

                        <Popover
                            anchorEl={anchorEl}
                            open={popoverOpen}
                            onClose={() => setAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{
                                sx: {
                                    borderRadius: '0.5rem',
                                    px: '0.75rem',
                                    py: '0.5rem',
                                    width: '50vw'
                                }
                            }}
                        >
                            <Box
                                display="flex"
                                gap="0.5rem"
                                height="2.75rem"
                                justifyContent="space-between"
                                alignItems="center"
                                onMouseLeave={() => setAnchorEl(null)}
                            >
                                <Typography width="30%" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                    {label || ''}
                                </Typography>
                                <Box
                                    height="100%"
                                    width="70%"
                                    display="flex"
                                    alignItems="center"
                                    sx={{ backgroundColor: theme.palette.primary[500], borderRadius: '0.5rem' }}
                                >
                                    <Link
                                        color={theme.palette.primary[400]}
                                        fontWeight={500}
                                        style={{
                                            textDecoration: 'none',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            padding: '0.75rem'
                                        }}
                                        href={getUrl(value)}
                                        target="_blank"
                                    >
                                        {value || ''}
                                    </Link>
                                </Box>
                            </Box>
                        </Popover>
                    </Box>
                );
            }
            default: {
                if (label === 'businessEntity') return <RenderEntity value={value} />;
                const formattedValue = getFormattedValue(dataType, value);
                return mainTypography(formattedValue || '');
            }
        }
    };

    const centerContent = ['checkbox', 'attachment', 'boolean'].includes(dataType);

    const getLabelWidth = () => {
        if (resizeField) return '100%';
        if (isRecordViewer) return '45%';
        return '30%';
    };

    const getValueWidth = () => {
        if (resizeField) return '100%';
        if (isRecordViewer) return '55%';
        return '70%';
    };

    const labelContentWidth = () => {
        if (isRecordViewer) {
            return {
                labelWidth: '70%',
                fieldMenuWidth: '30%'
            };
        }

        return {
            labelWidth: '80%',
            fieldMenuWidth: '20%'
        };
    };

    const getUrl = (url: string) => {
        if (url?.includes('http') || url?.includes('https')) {
            return url;
        }
        return `https://${url}`;
    };

    const fieldToMenu = objectName ? label : `${label}::${field.id}`;

    return (
        <Tooltip title={getFormattedValue(dataType, value)} placement="left" arrow>
            <Box
                id="render-view-field"
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                sx={{
                    width: '100%',
                    mb: '0.5rem',
                    opacity: isDragging ? 0.8 : 1,
                    cursor: isDraggable ? 'grab' : 'default',
                    '&:active': {
                        cursor: isDraggable ? 'grabbing' : 'default'
                    },
                    pointerEvents: ['string', 'textarea', 'url'].includes(dataType) ? 'auto' : 'none'
                }}
            >
                <Box
                    id="render-view-field-grid"
                    display="flex"
                    height="100%"
                    sx={{
                        flexDirection: resizeField ? 'column' : 'row',
                        alignItems: resizeField ? 'flex-start' : 'center',
                        gap: resizeField ? '0.5rem' : '0'
                    }}
                >
                    <Box
                        data-testid="render-view-field-label"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width={getLabelWidth()}
                        sx={{ pr: '0.1rem' }}
                    >
                        <Box
                            display="flex"
                            alignItems={resizeField ? 'flex-start' : 'center'}
                            justifyContent={resizeField ? 'space-between' : 'flex-start'}
                            gap={1}
                            width={labelContentWidth().labelWidth}
                        >
                            {isDraggable && (
                                <DragIndicator
                                    sx={{
                                        fontSize: '1.5rem',
                                        color: theme.palette.secondary[800],
                                        cursor: 'grab',
                                        '&:active': {
                                            cursor: 'grabbing'
                                        }
                                    }}
                                />
                            )}
                            <Typography
                                title={customLabel}
                                gutterBottom
                                sx={{ ...theme.typography.formLabel, whiteSpace: resizeField ? 'nowrap' : 'normal' }}
                            >
                                {customLabel || ''}:
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                            width={labelContentWidth().fieldMenuWidth}
                            sx={{ pointerEvents: 'auto' }}
                        >
                            {showFieldMenu && <FieldMenu field={fieldToMenu} objectName={objectName} />}
                        </Box>
                    </Box>
                    <Box
                        data-testid="render-view-field-value"
                        display="flex"
                        alignItems="center"
                        justifyContent={centerContent ? 'center' : 'flex-start'}
                        width={getValueWidth()}
                        sx={{
                            backgroundColor: theme.palette.primary[500],
                            borderRadius: '0.5rem',
                            px: '0.75rem',
                            minHeight: '2rem',
                            height: '2rem'
                        }}
                    >
                        {renderObjectProperty()}
                    </Box>
                </Box>
            </Box>
        </Tooltip>
    );
};
